import React, { useState } from 'react';
import { View, StyleSheet, Pressable, Image, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { strings } from '@/constants/residents/strings';
import { SignUpForm } from '@/features/auth/SignUpForm';
import { useSignUpForm } from '@/features/auth/useSignUpForm';
import { ResioLogo } from '@/assets/ResioLogo';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { useAppSelector } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useLegalContent } from '@/hooks/common/useLegalContent';
import { LegalModal } from '@/components/common/modals';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';

export const SignUpScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const state = useSignUpForm();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { isMobile, isTablet, isDesktop, isLarge, width } = useResponsive();
  const styles = useThemedStyles(makeStyles);

  // Check if we're in admin mode based on URL or app state
  const isAdminMode = false; // TODO: Determine this based on your app's logic

  const title = isAdminMode ? 'Welcome to Resio Community Portal!' : 'Welcome to Resio!';
  const subtitle = isAdminMode ? undefined : undefined;
  const image = isAdminMode ? require('@/assets/images/login.png') : require('@/assets/images/sign-up.png');

  const handleLogin = () => {
    navigation.navigate('Login');
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const { showLegalContent, modalVisible, modalContentType, closeModal } = useLegalContent();

  const handlePrivacyPolicy = () => {
    showLegalContent('privacy');
  };

  const handleTermsOfService = () => {
    showLegalContent('terms');
  };

  // Dynamic styles based on admin mode and responsive breakpoints
  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isTablet && styles.splitContainerTablet,
      isDesktop && styles.splitContainerDesktop
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isTablet && styles.imagePaneTablet,
      isDesktop && styles.imagePaneDesktop
    ],
    formPane: [
      styles.formPane,
      isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      isMobile && styles.formPaneMobile,
      isTablet && styles.formPaneTablet,
      isDesktop && styles.formPaneDesktop
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isTablet && styles.formInnerTablet,
      isDesktop && styles.formInnerDesktop
    ],
    title: [
      styles.title,
      isAdminMode ? styles.titleAdmin : styles.titleResident,
      isMobile && styles.titleMobile,
      isDesktop && styles.titleDesktop
    ],
    loginRow: [
      styles.loginRow,
      isAdminMode ? styles.loginRowAdmin : styles.loginRowResident,
      isMobile && styles.loginRowMobile
    ],
    link: [
      styles.link,
      isAdminMode ? styles.linkAdmin : styles.linkResident
    ]
  };

  return (
    <View style={dynamicStyles.pageWrapper}>
      {/* Header with Resio Logo - Always show during auth flow */}
      {(
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <ResioLogo 
              width={64} 
              height={64} 
              style={styles.logoIcon}
            />
            <Text style={styles.logoText}>RESIO</Text>
          </View>
        </View>
      )}

      <View style={dynamicStyles.splitContainer}>
        {/* Left side - Image pane (hidden on mobile, visible on desktop) */}
        <View style={dynamicStyles.imagePane}>
          <Image
            source={image}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
        
        {/* Right side - Form pane */}
        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            {/* Title */}
            <Text style={dynamicStyles.title}>{title}</Text>
            {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

            {/* Sign Up Form */}
            <SignUpForm 
              state={state} 
              onLogin={handleLogin}
              onBack={handleBack}
              onPrivacyPolicy={handlePrivacyPolicy}
              onTermsOfService={handleTermsOfService}
            />

            {/* Already have account link */}
            <Text style={dynamicStyles.loginRow}>
              Already have an account?{' '}
              <Pressable onPress={handleLogin}>
                <Text style={dynamicStyles.link}>Sign in</Text>
              </Pressable>
            </Text>

            {/* Footer text - Only show for residents */}
            {!isAdminMode && (
              <Text style={styles.footerText}>
                By signing up, you agree to our{' '}
                <Pressable onPress={handleTermsOfService}>
                  <Text style={dynamicStyles.link}>Terms of Service</Text>
                </Pressable>{' '}
                and{' '}
                <Pressable onPress={handlePrivacyPolicy}>
                  <Text style={dynamicStyles.link}>Privacy Policy</Text>
                </Pressable>
              </Text>
            )}

            <Pressable accessibilityRole="button" onPress={state.submit} style={{ display: 'none' }} />
          </View>
        </View>
      </View>

      {/* Legal Modal - Only renders on web */}
      <LegalModal
        visible={modalVisible}
        contentType={modalContentType}
        onClose={closeModal}
      />
    </View>
  );
};

export const SignUpDesktopContent: React.FC = () => {
  const navigation = useNavigation<any>();
  const state = useSignUpForm();
  const { showLegalContent, modalVisible, modalContentType, closeModal } = useLegalContent();
  const styles = useThemedStyles(makeStyles);

  const isAdminMode = false;

  const title = isAdminMode ? 'Welcome to Resio Community Portal!' : 'Welcome to Resio!';
  const subtitle = isAdminMode ? undefined : undefined;
  const link = [styles.link, isAdminMode ? styles.linkAdmin : styles.linkResident];
  const loginRow = [styles.loginRow, isAdminMode ? styles.loginRowAdmin : styles.loginRowResident];
  const titleStyle = [styles.title, styles.titleDesktop, isAdminMode ? styles.titleAdmin : styles.titleResident];

  const handleLogin = () => navigation.navigate('Login');
  const handleBack = () => navigation.goBack();
  const handlePrivacyPolicy = () => showLegalContent('privacy');
  const handleTermsOfService = () => showLegalContent('terms');

  return (
    <View style={desktopContentStyles.formInner}>
      <Text style={titleStyle}>{title}</Text>
      {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

      <SignUpForm
        state={state}
        onLogin={handleLogin}
        onBack={handleBack}
        onPrivacyPolicy={handlePrivacyPolicy}
        onTermsOfService={handleTermsOfService}
      />

      <Text style={loginRow}>
        Already have an account?{' '}
        <Pressable onPress={handleLogin}>
          <Text style={link}>Sign in</Text>
        </Pressable>
      </Text>

      {!isAdminMode && (
        <Text style={styles.footerText}>
          By signing up, you agree to our{' '}
          <Pressable onPress={handleTermsOfService}>
            <Text style={link}>Terms of Service</Text>
          </Pressable>{' '}
          and{' '}
          <Pressable onPress={handlePrivacyPolicy}>
            <Text style={link}>Privacy Policy</Text>
          </Pressable>
        </Text>
      )}

      <Pressable accessibilityRole="button" onPress={state.submit} style={{ display: 'none' }} />

      <LegalModal visible={modalVisible} contentType={modalContentType} onClose={closeModal} />
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  // Base styles
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  // Header styles - matching web apps (p-4 = 16px padding)
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16, // p-4 equivalent
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary, // gray-700
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 10,
      },
    }),
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    marginRight: 8, // mr-2 equivalent
    // For unauthenticated state: h-16 w-16 (64px × 64px)
    width: 64,
    height: 64,
  },
  logoText: {
    // For unauthenticated state: text-5xl (48px)
    fontSize: Platform.select({ web: 48, default: 32 }),
    fontWeight: '600', // font-semibold
    color: colors.text.primary, // White text for dark theme
    letterSpacing: 0.5,
  },
  // Admin-specific page wrapper
  pageWrapperAdmin: {
    backgroundColor: colors.bg.base, // Dark background for admin
  },
  // Resident-specific page wrapper
  pageWrapperResident: {
    backgroundColor: colors.bg.base, // Dark background for resident
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary, // gray-700
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  // Mobile: Stack vertically, no image
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  // Tablet: Still stack vertically but with more space
  splitContainerTablet: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 768,
  },
  // Desktop: Side by side layout
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },
  imagePane: {
    ...Platform.select({
      web: {
        display: 'flex' as any,
        width: 384,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
      },
      default: {
        display: 'none',
      },
    }),
  },
  // Mobile: Hidden
  imagePaneMobile: {
    display: 'none',
  },
  // Tablet: Hidden (following original web app pattern)
  imagePaneTablet: {
    display: 'none',
  },
  // Desktop: Visible with fixed width
  imagePaneDesktop: {
    display: 'flex' as any,
    width: 384,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && { objectFit: 'cover' as any }),
  },
  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'center', default: 'flex-start' }),
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  // Mobile: Full width, no borders, padding for mobile
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  // Tablet: Constrained width, centered
  formPaneTablet: {
    width: '100%',
    maxWidth: 512,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  // Desktop: Fixed width with borders
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'center',
    flexShrink: 0,
  },
  // Admin-specific form pane
  formPaneAdmin: {
    backgroundColor: colors.bg.base, // Dark background
  },
  // Resident-specific form pane
  formPaneResident: {
    backgroundColor: colors.bg.base, // Dark background
  },
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 0, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    }),
  },
  // Mobile: Reduced padding, full width
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  // Tablet: Medium padding
  formInnerTablet: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    maxWidth: 480,
  },
  // Desktop: Original padding
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 0,
    maxWidth: 512,
  },
  title: {
    fontSize: Platform.select({ web: 30, default: 24 }),
    fontWeight: '600',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    marginBottom: 24,
    marginTop: 24,
  },
  // Mobile: Center aligned, smaller font
  titleMobile: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  // Desktop: Left aligned, larger font (matching web apps)
  titleDesktop: {
    fontSize: 30,
    textAlign: 'left',
    marginBottom: 24,
  },
  // Admin-specific title
  titleAdmin: {
    color: colors.text.primary, // White text for dark theme
  },
  // Resident-specific title
  titleResident: {
    color: colors.text.primary, // White text for dark theme
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: colors.text.muted,
    marginBottom: 16,
  },
  loginRow: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 16,
    fontWeight: '500',
  },
  // Mobile: Additional top padding (matching resio-web max-sm:pt-4)
  loginRowMobile: {
    marginTop: 20, // pt-4 equivalent
  },
  // Admin-specific login row (not used but kept for consistency)
  loginRowAdmin: {
    color: colors.text.primary, // White text
  },
  // Resident-specific login row
  loginRowResident: {
    color: colors.text.primary, // White text
  },
  footerText: {
    fontSize: 12,
    color: colors.text.muted,
    textAlign: 'center',
    marginTop: 16,
  },
  link: {
    textDecorationLine: 'underline',
    fontSize: 14,
    fontWeight: '500',
  },
  // Admin-specific link
  linkAdmin: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
  // Resident-specific link
  linkResident: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
});
